import {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useMemo,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import useLocalStorageState from 'use-local-storage-state';
import { _getToken } from 'src/Config/axiosApi';
import { AUTH_DISABLED } from 'src/App/Providers';
import { MatchedByType, PredictionDelta, PredictionOpportunity, WSPredictionResponse } from '../types';
import { predictionsReducer, initialState } from './predictionsReducer';
import { UNCATEGORIZED } from '../constants/categories';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { useOpportunitiesSnapshot } from '../api/useOpportunitiesSnapshot';

interface PredictionsContextProps {
  /** Active (non-archived) opportunities, post category filter. */
  opportunities: PredictionOpportunity[];
  /** Archived opportunities, post category filter — rendered in the Archived tab. */
  archivedOpportunities: PredictionOpportunity[];
  isConnected: boolean;
  /** Count of Active opportunities (post category filter). */
  count: number;
  /** Count of Archived opportunities (post category filter). */
  archivedCount: number;
  removePair: (pairId: string) => void;
  confirmPair: (pairId: string, matchedBy: MatchedByType) => void;
  excludedCategories: string[];
  setExcludedCategories: (categories: string[]) => void;
  categoryCounts: Record<string, number>;
}

const PredictionsContext = createContext<PredictionsContextProps | undefined>(
  undefined
);

export const PredictionsProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = _getToken();
  const { isPredictionsRunning } = useUpdates();

  const [state, dispatch] = useReducer(predictionsReducer, initialState);

  const [excludedCategories, setExcludedCategories] = useLocalStorageState<string[]>(
    'V3.PredictionFilters.excludedCategories',
    { defaultValue: [] }
  );

  const allOpportunities = useMemo(
    () => Array.from(state.cache.values()),
    [state.cache]
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const o of allOpportunities) {
      const key = o.category ?? UNCATEGORIZED;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [allOpportunities]);

  const filteredByCategory = useMemo(() => {
    if (excludedCategories.length === 0) return allOpportunities;
    const set = new Set(excludedCategories);
    return allOpportunities.filter(
      o => !set.has(o.category ?? UNCATEGORIZED)
    );
  }, [allOpportunities, excludedCategories]);

  const opportunities = useMemo(
    () => filteredByCategory.filter(o => !o.archived),
    [filteredByCategory]
  );

  const archivedOpportunities = useMemo(
    () => filteredByCategory.filter(o => !!o.archived),
    [filteredByCategory]
  );

  const removePair = useCallback((pairId: string) => {
    dispatch({ type: 'REMOVE_PAIR', pairId });
  }, []);

  const confirmPair = useCallback((pairId: string, matchedBy: MatchedByType) => {
    dispatch({ type: 'CONFIRM_PAIR', pairId, matchedBy });
  }, []);

  const { socket } = useSocket(
    import.meta.env.VITE_PREDICTIONS_WS_URL + '/predictions',
    {
      enabled: isPredictionsRunning && (AUTH_DISABLED || !!accessToken),
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() })
    }
  );

  // Listen for prediction events
  const { lastMessage: snapshotMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_snapshot'
  );

  const { lastMessage: updateMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_update'
  );

  const { lastMessage: deltaMessage } = useSocketEvent<PredictionDelta>(
    socket,
    'prediction_delta'
  );

  const hasSubscribed = useRef(false);

  // Seed the cache from the REST snapshot endpoint. The WS `prediction_snapshot`
  // event the gateway emits inside `handleConnection` races against React's
  // effect timing (socket.io-react-hook installs listeners in a useEffect that
  // fires AFTER the snapshot frame already arrived), so the snapshot was being
  // dropped silently. REST fetch closes the race; the WS remains the source of
  // truth for live deltas/updates.
  const { data: snapshotData } = useOpportunitiesSnapshot(
    isPredictionsRunning && (AUTH_DISABLED || !!accessToken)
  );

  useEffect(() => {
    if (!snapshotData) return;
    dispatch({
      type: 'SNAPSHOT',
      data: snapshotData.data,
      count: snapshotData.count,
      timestamp: snapshotData.timestamp ?? Date.now()
    });
  }, [snapshotData]);

  // When predictions are OFF, clear stale opportunities from the table.
  useEffect(() => {
    if (isPredictionsRunning) return;
    dispatch({
      type: 'SNAPSHOT',
      data: [],
      count: 0,
      timestamp: Date.now()
    });
  }, [isPredictionsRunning]);

  // Handle connection — subscribe on connect
  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }

    if (!hasSubscribed.current) {
      socket.emit('subscribe_predictions');
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  // Handle snapshot messages
  useEffect(() => {
    if (snapshotMessage?.type === 'SNAPSHOT') {
      dispatch(snapshotMessage);
    }
  }, [snapshotMessage]);

  // Handle update messages
  useEffect(() => {
    if (updateMessage?.type === 'UPDATE') {
      dispatch(updateMessage);
    }
  }, [updateMessage]);

  // Handle per-opportunity delta updates (event-driven hot path)
  useEffect(() => {
    if (
      deltaMessage?.type === 'DELTA_UPDATE' ||
      deltaMessage?.type === 'DELTA_REMOVE'
    ) {
      dispatch(deltaMessage);
    }
  }, [deltaMessage]);

  // Periodic cleanup — remove markets not updated in 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'CLEANUP' });
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const isConnected = isPredictionsRunning && socket.connected;

  return (
    <PredictionsContext.Provider
      value={{
        opportunities,
        archivedOpportunities,
        isConnected,
        count: opportunities.length,
        archivedCount: archivedOpportunities.length,
        removePair,
        confirmPair,
        excludedCategories,
        setExcludedCategories,
        categoryCounts
      }}
    >
      {children}
    </PredictionsContext.Provider>
  );
};

export const usePredictions = () => {
  const context = useContext(PredictionsContext);
  if (!context) {
    throw new Error('usePredictions must be used within a PredictionsProvider');
  }
  return context;
};
